import { ArrowLeft, Copy05 } from '@untitled-ui/icons-react/build/cjs';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ActivityFeedCmp from 'src/components/ActivityFeedCmp';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import PageSectionLayout from 'src/components/PageSectionLayout';
import Header from 'src/components/PageSectionLayout/Header/Header';
import TabButton from 'src/components/TabButton';
import TooltipCmp from 'src/components/TooltipCmp';
import { getBadgeLabelByOrderStatus } from 'src/constants/common';
import { PATH } from 'src/constants/path';
import { ROUTES } from 'src/constants/routes';
import { useOrderLiveSync } from 'src/hooks/useOrderLiveSync';
import { BadgeType } from 'src/interface/common';
import {
    getOrderDetailsAsync,
    orderDetailsData,
    orderDetailsError,
    orderDetailsLoading,
    resetOrderDetailsState,
    selectCurrentLegData,
    selectCurrentLocation,
    setLegDocumentsState,
} from 'src/redux/orders.slice';
import { activitiesPaginationData, appendActivities, setActivities, setActivitiesLoading, setActivitiesPagination } from 'src/redux/Quoting.slice';
import { AppDispatch, RootState } from 'src/redux/store';
import { getQuoteActivities } from 'src/services/QuoteService';
import WalToast from 'src/utils/WalToast';

import OrderHeaderActions from '../Common/OrderHeaderActions';
import OrderInfoSection from '../Common/OrderInfoSection';
import { classOptions } from '../order.const';
import { findLatestBolDocument } from '../order.helpers';
import OrderStatus from '../OrderStatus';

import LegOverviewSection from './LegOverviewSection';
import OrderDetailsWidgets from './OrderDetailsWidgets';
import OrderLegDetailsSection from './OrderLegDetailsSection';
import OrderMessages from './OrderMessages';

// Helper function to get badge type based on order status
export const getOrderStatusBadgeType = (status: string): BadgeType => {
    if (status === 'draft' || status === 'cancelled' || status === 'dispatched') {
        return 'pink';
    }

    if (status === 'booked' || status === 'delivered') {
        return 'lightblue';
    }

    if (status === 'in-transit' || status === 'booking_failed') {
        return 'error';
    }

    if (status === 'completed') {
        return 'gray';
    }

    return 'success';
};

const OrderView = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams();

    // Redux selectors
    const isLoading = useSelector(orderDetailsLoading);
    const order = useSelector(orderDetailsData);
    const error = useSelector(orderDetailsError);
    const currentLocation = useSelector(selectCurrentLocation);
    const currentLegData = useSelector(selectCurrentLegData);
    const unreadMessageCount = useSelector((state: any) => state.orderChat.unreadCount);
    // Chat is unavailable for WAL staff sessions (no contact participant row) — same gate the
    // Messages widget uses. Without it the tab mounts the chat and every fetch comes back 403.
    const isWalUserSession = useSelector((state: RootState) => !!state.auth.currentUser?.isWalUserSession);
    const pagination = useSelector(activitiesPaginationData);

    const [activeOuterTab, setActiveOuterTab] = useState(() => (location.state?.openMessagesTab ? 'messages' : 'overview'));

    useEffect(
        () => () => {
            dispatch(setActivities([]));
            dispatch(setActivitiesPagination(null));
        },
        []
    );

    // When navigating here from a message notification while already mounted, switch to messages tab
    useEffect(() => {
        if (location.state?.openMessagesTab) {
            setActiveOuterTab('messages');
        }
    }, [location.state?.openMessagesTab]);

    // Deep link from the dashboard's "Review Your Bill of Lading" action item: open the BOL
    // straight into the document preview modal instead of dropping the customer on the page with
    // the actual review still to be hunted down. Waits for the order payload because the BOL is
    // found in its documents. The modal itself is rendered by LegDocumentsSection off this
    // redux field, so the overview tab (the default on arrival) must be the active one.
    const bolDeepLinkHandledRef = useRef(false);

    useEffect(() => {
        if (!location.state?.openBolPreview || bolDeepLinkHandledRef.current) return;
        if (isLoading || !order) return;

        bolDeepLinkHandledRef.current = true;
        // Consume the flag so a refresh or a back-navigation to this order doesn't reopen the
        // modal — react-router keeps location state in history.state across reloads.
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });

        const bolDocument = findLatestBolDocument(order);

        // No BOL on the order (it can be removed between the dashboard fetch and the click) —
        // leave the customer on the detail page rather than popping an empty modal.
        if (bolDocument) dispatch(setLegDocumentsState({ viewDocument: bolDocument }));
    }, [location.state?.openBolPreview, isLoading, order]);

    const fetchActivities = useCallback(
        async ({ lastId, append, silent = false }: { lastId?: number | null; append: boolean; silent?: boolean }) => {
            if (!order?.id) return;

            // A silent pass is the socket live-sync refreshing in the background — skip the
            // loading flag so the feed doesn't blink into skeletons while the customer reads it.
            if (!silent) dispatch(setActivitiesLoading(true));

            try {
                const response = await getQuoteActivities({ entityType: 'order', entityId: order.id, perPage: 25, ...(lastId ? { lastId } : {}) });
                const data = response?.data;

                if (append) {
                    dispatch(appendActivities(data?.activities || []));
                } else {
                    dispatch(setActivities(data?.activities || []));
                }

                dispatch(
                    setActivitiesPagination(
                        data?.pagination
                            ? {
                                  hasMore: data.pagination.hasMore ?? false,
                                  nextCursor: data.pagination.nextCursor ?? null,
                                  totalItems: data.pagination.totalItems ?? 0,
                              }
                            : null
                    )
                );
            } catch (err: any) {
                // Same reasoning as the loading flag — an unrequested background refresh must
                // not raise a toast the customer has no context for.
                if (!silent) WalToast.error(err?.response?.data?.message || 'Failed to load activities');
            } finally {
                if (!silent) dispatch(setActivitiesLoading(false));
            }
        },
        [order?.id, dispatch]
    );

    useEffect(() => {
        if (!order?.id) return;

        fetchActivities({ append: false });
    }, [order?.id]);

    const handleLoadMore = useCallback(async () => {
        if (!pagination?.hasMore || !pagination?.nextCursor) return;

        fetchActivities({ lastId: pagination.nextCursor, append: true });
    }, [pagination, fetchActivities]);

    useEffect(() => {
        if (id) {
            dispatch(getOrderDetailsAsync(id));
        } else {
            navigate(ROUTES.ORDERS);
        }

        return () => {
            dispatch(resetOrderDetailsState());
        };
    }, [id]);

    useEffect(() => {
        if (error?.response?.status === 404 || error?.response?.data?.code === 'E_MISSING_OR_INVALID_PARAMS') {
            navigate(ROUTES.ORDERS);
        }
    }, [error]);

    // Live sync with the WAL side: when ops approves / rejects / dismisses one of this
    // customer's requests (edit, cancellation, location update) — or posts a new location —
    // re-read the order in the background so the page reflects it without a hard refresh.
    // Silent on both calls: the customer did not ask for this fetch, so nothing may blink
    // into skeletons, reset their leg tab, or toast at them. See useOrderLiveSync.
    const handleLiveSyncRefresh = useCallback(() => {
        if (!id) return;

        dispatch(getOrderDetailsAsync({ id, silent: true }));
        // Approvals also write an order activity, and the feed is cursor-paginated, so it
        // has to be re-read from the top rather than patched.
        fetchActivities({ append: false, silent: true });
    }, [id, dispatch, fetchActivities]);

    useOrderLiveSync(id, handleLiveSyncRefresh);

    const copyDebounce = useCallback(
        debounce((copyQuote: string) => {
            navigator.clipboard.writeText(copyQuote);
            WalToast.copy(t('Common.Copied_to_clipboard'), '', 'copy-msg');
        }, 0),
        []
    );

    const memorizedBadge = useMemo(() => {
        const showLoading = isLoading || !order;
        const badgeType = id && order?.orderStatus ? getOrderStatusBadgeType(order?.orderStatus) : 'success';
        const badgeText = !showLoading && order?.orderStatus ? getBadgeLabelByOrderStatus(order?.orderStatus) : t('Common.Opened');
        const mainClassName = `bg-white rounded-md ${showLoading ? 'custom-loading' : ''}`;

        return (
            <BadgeCmp style="modern" mainClassName={mainClassName} type={badgeType as BadgeType}>
                {badgeText}
            </BadgeCmp>
        );
    }, [id, order, isLoading, t]);

    const memorizedOrderTypeBadge = useMemo(() => {
        const showLoading = isLoading || !order;
        const orderTypeValue = order?.orderType || 'ltl';

        const badgeType: BadgeType = 'success';
        const badgeText = !showLoading && orderTypeValue ? classOptions?.find((option: any) => option?.value === orderTypeValue)?.label : t('Common.LTL');

        const mainClassName = `bg-white rounded-md ${showLoading ? 'custom-loading' : ''}`;

        return (
            <BadgeCmp style="modern" mainClassName={mainClassName} type={badgeType}>
                {badgeText}
            </BadgeCmp>
        );
    }, [order, isLoading, t]);

    const outerTabArray = useMemo(
        () => [
            { value: 'overview', name: t('Common.Overview') },
            // Messages is dropped entirely for a WAL staff session — the portal backend refuses
            // every chat endpoint for it, so the tab could only ever show a broken, empty thread.
            ...(isWalUserSession
                ? []
                : [
                      {
                          value: 'messages',
                          name: t('Common.Messages'),
                          count: unreadMessageCount || 0,
                          showDotAboveCount: unreadMessageCount > 0,
                      },
                  ]),
            { value: 'activity', name: t('Common.Activity') },
        ],
        [t, unreadMessageCount, isWalUserSession]
    );

    const isOrderIdLoading = isLoading || !order;

    return (
        <PageSectionLayout
            headerClassName="stepper-container w-full z-[99]"
            contentClassName="stepper-container overflow-hidden !p-0 !gap-0"
            header={
                <Header
                    title={
                        <div className="flex items-center md:gap-2 gap-1">
                            <ButtonCmp
                                className={isLoading ? 'custom-loading btn_secondary_black' : 'btn_secondary_black'}
                                size="square-sm"
                                onClick={() => {
                                    navigate(PATH.ORDER_VIEW, { replace: true });
                                }}
                            >
                                <ArrowLeft className="w-4 h-4" />
                            </ButtonCmp>
                            <h6 className={`text-grayLight900 text-lg font-semibold ${isOrderIdLoading ? 'custom-loading' : ''}`}>{isOrderIdLoading ? '#WAL-XXXX-1' : `#${order?.orderId}`}</h6>
                        </div>
                    }
                    leftContent={
                        <>
                            {isOrderIdLoading ? (
                                <div className="w-5 h-5 rounded custom-loading" />
                            ) : (
                                <TooltipCmp message={t('Common.Copy')}>
                                    <Copy05
                                        className={`w-5 h-5 text-primary cursor-pointer font-bold`}
                                        onClick={() => {
                                            copyDebounce(`#${order?.orderId}`);
                                        }}
                                    />
                                </TooltipCmp>
                            )}

                            {memorizedBadge}
                            {memorizedOrderTypeBadge}
                        </>
                    }
                    mainContainerClassName="xls:!flex-nowrap !flex-wrap mdm:!flex-row md:!flex-col sm:!flex-row flex-col mdm:!items-center !items-start"
                    rightSideContent={<OrderHeaderActions orderDbId={id as string} />}
                />
            }
        >
            <div className="px-[var(--layout-page-content-left-spacing)] pt-[var(--layout-page-content-top-spacing)]">
                <OrderInfoSection alwaysShowFullDate />
            </div>
            <div className="flex overflow-auto custom-scrollbar-main flex-1 ">
                <div className="flex-1 flex flex-col overflow-hidden pl-[var(--layout-page-content-left-spacing)] pr-2 pt-5">
                    <div
                        className={`flex-1 flex flex-col -mx-2 px-2 pb-4 ${
                            activeOuterTab === 'messages' ? 'overflow-hidden pb-4 space-y-5' : 'overflow-auto custom-scrollbar-v2 space-y-5'
                        } space-y-5 ${activeOuterTab === 'overview' ? 'scroll-padding lg:!pb-[180px]' : ''}`}
                    >
                        <div className="sticky top-0 z-[11] global-background-color">
                            <TabButton
                                parentClassName="tab-border-bottom-parent w-full !border-b !border-borderPrimary"
                                tabParentClassName="lg:px-1 lg:-mx-1 global-background-color"
                                className={`tab-border-bottom min-w-[200px] !pt-[6px]`}
                                activeClassName="tab-border-bottom-active"
                                tabBadgeClassName="tab-border-bottom-badge"
                                tabArray={outerTabArray}
                                handleOnClick={(e: any) => setActiveOuterTab(e?.target?.dataset?.value)}
                                isActive={activeOuterTab}
                            />
                        </div>
                        {activeOuterTab === 'overview' && (
                            <>
                                <OrderStatus isLoading={isLoading} order={order} currentLegData={currentLegData} currentLocation={currentLocation} />
                                <LegOverviewSection />
                                <OrderLegDetailsSection />
                            </>
                        )}
                        {/* Always mounted so data pre-loads on page load; min-h-0 allows flex child to shrink.
                            Not mounted at all for a WAL staff session — mounting it fires chat fetches the
                            backend answers with 403 "Chat is not available for WAL staff sessions." */}
                        {!isWalUserSession && (
                            <div className={activeOuterTab === 'messages' ? 'flex-1 min-h-0 flex flex-col' : 'hidden'}>
                                <OrderMessages orderId={id!} mainLoading={isLoading} isActive={activeOuterTab === 'messages'} />
                            </div>
                        )}
                        {activeOuterTab === 'activity' && (
                            <ActivityFeedCmp entityType="order" entityId={order?.id} handleLoadMore={handleLoadMore} notePlaceholder="Add an activity or note for this order" isActivityBox="sm" />
                        )}
                    </div>
                </div>
                <div className="widget-right-sidebar-common-wrap flex flex-col overflow-auto custom-scrollbar-v2 space-y-5 pt-5">
                    <OrderDetailsWidgets />
                </div>
            </div>
        </PageSectionLayout>
    );
};

export default OrderView;
